import { getSession } from "@/auth/session";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

export class ApiError extends Error {
	readonly status: number;
	readonly code: string;

	constructor(status: number, message: string, code = "error") {
		super(message);
		this.status = status;
		this.code = code;
	}
}

type Listener = (error: ApiError) => void;
const unauthorizedListeners = new Set<Listener>();

/** AuthProvider subscribes so any 401/403 on an admin call (expired token, revoked admin) logs out. */
export const onUnauthorized = (listener: Listener) => {
	unauthorizedListeners.add(listener);
	return () => {
		unauthorizedListeners.delete(listener);
	};
};

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
	const headers: Record<string, string> = { "Content-Type": "application/json" };
	const token = getSession()?.token;
	if (token) headers.Authorization = `Bearer ${token}`;
	if (init.headers) Object.assign(headers, init.headers);

	const res = await fetch(`${apiBaseUrl}${path}`, { ...init, headers });
	if (!res.ok) {
		const text = await res.text();
		let message = "Request failed";
		let code = "error";
		try {
			const json = JSON.parse(text) as { message?: string; code?: string };
			if (json.message) message = json.message;
			if (json.code) code = json.code;
		} catch {
			if (text) message = text;
		}
		const error = new ApiError(res.status, message, code);
		const lostAccess =
			res.status === 401 ||
			(res.status === 403 && path.startsWith("/admin/") && code === "forbidden");
		if (token && lostAccess) for (const listener of unauthorizedListeners) listener(error);
		throw error;
	}
	return res.json() as Promise<T>;
}

export const send = <T>(path: string, body: unknown, method = "POST") =>
	apiFetch<T>(path, { method, body: JSON.stringify(body) });

/** Builds `?a=1&b=2`, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>) {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "" || value === false) continue;
		search.set(key, String(value));
	}
	const out = search.toString();
	return out ? `?${out}` : "";
}

export const errorMessage = (error: unknown) =>
	error instanceof Error ? error.message : "Something went wrong";
